import { Component, Inject, OnInit } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, Observable } from 'rxjs';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertCardComponent } from '../../../../../../shared/components/alert-card/alert-card.component';
import { LoadingBoxComponent } from '../../../../../../shared/components/loading-box/loading-box.component';
import { UiTagComponent } from '../../../../../../shared/components/ui-tag/ui-tag.component';
import { ConfirmModalComponent } from '../../../../../../shared/components/confirm-modal/confirm-modal.component';
import { Perfil } from '../../../../../../shared/services/models/usuario/perfil.model';
import { PerfilService } from '../../../../../../shared/services/apis/usuario/perfil.service';
import { CrmEtapasKanbanService } from '../../../../../../shared/services/apis/vendas/crm-etapas-kanban.service';
import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { CrmEtapaOportunidade } from '../../../../../../shared/services/models/vendas/crm-etapa-oportunidade.model';
import {
  CrmCampoDefinicaoRequest,
  CrmMovimentacaoEtapaRequest,
  CrmPermissaoPerfilRequest,
} from '../../../../../../shared/services/requests/vendas/crm-configuracao-etapa-kanban.request';
import { GetCrmConfiguracaoEtapaResponse } from '../../../../../../shared/services/responses/vendas/crm-configuracao-etapa-kanban.response';

export interface EtapaOportunidadeModalData {
  etapa: CrmEtapaOportunidade | null;
  todasEtapas: CrmEtapaOportunidade[];
  // A página orquestra a persistência (etapa + sub-configurações) e devolve se deu certo.
  onSalvar?: (dados: EtapaOportunidadeModalResult) => Observable<boolean>;
}

// Payload emitido ao fechar com "Salvar" — a página orquestra a persistência.
export interface EtapaOportunidadeModalResult {
  salvou: boolean;
  excluiu?: boolean;
  isNova: boolean;
  etapa: CrmEtapaOportunidade;
  campos: CrmCampoDefinicaoRequest[];
  movimentacoes: CrmMovimentacaoEtapaRequest[];
  permissoes: CrmPermissaoPerfilRequest[];
  idEtapaRetornoReabertura: number | null;
}

export interface CampoOpcaoEtapa {
  idCampoDefinicaoOpcao?: number | null;
  descricao: string;
}

export interface CampoEtapa {
  idCampoDefinicao?: number | null;
  nome: string;
  tipo: string;
  obrigatorio: boolean;
  // Campo sistêmico do backend (Resultado/Motivo em Encerrado): editável, não removível.
  isEssencial?: boolean;
  opcoes: CampoOpcaoEtapa[];
  // public.tipo_entidade_vinculo — só preenchido quando tipo = 'link'.
  idTipoEntidadeVinculo?: number | null;
}

// key = uso interno; id = public.acao_etapa (4 Reabrir é exclusivo do CRM).
export interface AcaoEtapa {
  key: string;
  id: number;
  label: string;
  icon: string;
}

interface DestinoConfig {
  selecionado: boolean;
  perfis: number[];
}

interface DestinoEtapa {
  etapa: CrmEtapaOportunidade;
  config: DestinoConfig;
}

@Component({
  selector: 'app-etapa-oportunidade-modal',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    DragDropModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    AlertCardComponent,
    LoadingBoxComponent,
    UiTagComponent,
  ],
  templateUrl: './etapa-oportunidade-modal.component.html',
  styleUrls: ['./etapa-oportunidade-modal.component.css'],
})
export class EtapaOportunidadeModalComponent implements OnInit {
  public readonly NOME_MAX_LENGTH = 40;

  public readonly cores = [
    '#1E5DB8',
    '#7A4FBF',
    '#0F7A8B',
    '#C26A00',
    '#B73232',
    '#1E8E5A',
    '#5E6A2E',
    '#2E7D6B',
    '#4B5563',
    '#D93025',
  ];

  public readonly icones = [
    'person_search',
    'fact_check',
    'forum',
    'request_quote',
    'sell',
    'flag',
    'handshake',
    'call',
    'mail',
    'event',
    'trending_up',
    'workspace_premium',
    'attach_money',
    'gavel',
    'task_alt',
    'cancel',
  ];

  // Os 9 tipos da story (Seção 2). key = uso interno; id = public.tipo_campo
  // (ATS.Hub.API.CRM.Enums.TipoCampoEnum) — daí Dropdown apontar para 5 (Seleção),
  // Toggle para 9 (Sim/Não) e Upload de arquivo para 8 (Anexo).
  public readonly tiposCampo = [
    { key: 'text', id: 1, label: 'Texto' },
    { key: 'textarea', id: 10, label: 'Texto longo' },
    { key: 'select', id: 5, label: 'Dropdown' },
    { key: 'date', id: 4, label: 'Data' },
    { key: 'currency', id: 3, label: 'Moeda' },
    { key: 'percent', id: 11, label: 'Percentual' },
    { key: 'boolean', id: 9, label: 'Toggle' },
    { key: 'link', id: 7, label: 'Vínculo de entidade' },
    { key: 'anexo', id: 8, label: 'Upload de arquivo' },
  ];

  // public.tipo_entidade_vinculo (TipoEntidadeVinculoEnum).
  public readonly vinculoEntidades = [
    { id: 1, label: 'Pessoa' },
    { id: 2, label: 'Produto' },
    { id: 3, label: 'Usuário' },
  ];

  public readonly acoes: AcaoEtapa[] = [
    { key: 'view', id: 1, label: 'Visualizar', icon: 'visibility' },
    { key: 'edit', id: 2, label: 'Editar', icon: 'edit' },
    { key: 'cancel', id: 3, label: 'Cancelar', icon: 'cancel' },
    { key: 'reabrir', id: 4, label: 'Reabrir', icon: 'restart_alt' },
  ];

  public isNova: boolean;
  public chave: string;
  public isCarregando = false;
  public isSalvando = false;

  public perfis: Perfil[] = [];

  // Estado do formulário
  public nome = '';
  public cor = '#1E5DB8';
  public icone = 'sell';
  public descricao = '';
  // A API decide Reabrir e a etapa de retorno por is_final (não por isEspecial): uma etapa
  // final pode não ser a especial "Encerrado" (ex.: "Cancelado"). Guardado à parte, no mesmo
  // padrão de nome/cor/icone, para refletir o estado atual do formulário — inclusive numa
  // etapa nova marcada como final na mesma sessão de edição.
  public isFinal = false;

  public campos: CampoEtapa[] = [];
  public destinos: DestinoEtapa[] = [];
  // permissoes[idPerfil][acaoKey] = boolean
  public permissoes: { [idPerfil: number]: { [acao: string]: boolean } } = {};
  public idEtapaRetornoReabertura: number | null = null;

  public filtroPerfil = '';

  constructor(
    public dialogRef: MatDialogRef<EtapaOportunidadeModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EtapaOportunidadeModalData,
    private perfilService: PerfilService,
    private crmEtapasKanbanService: CrmEtapasKanbanService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) {
    const etapa = data.etapa;
    this.isNova = !etapa;
    this.chave = etapa?.chave ?? '';

    if (etapa) {
      this.nome = etapa.nome;
      this.cor = etapa.cor;
      this.icone = etapa.icone;
      this.descricao = etapa.descricao ?? '';
      this.idEtapaRetornoReabertura = etapa.idEtapaKanbanRetornoReabertura ?? null;
      this.isFinal = etapa.isFinal;
    }

    this.destinos = (data.todasEtapas || [])
      .filter(e => !etapa || e.idEtapaKanban !== etapa.idEtapaKanban)
      .map(e => ({ etapa: e, config: { selecionado: false, perfis: [] } }));
  }

  ngOnInit(): void {
    const perfis$ = this.perfilService.getAllLookup();

    // Etapa nova: não há configuração a carregar. Por padrão todos os perfis podem
    // tudo (default-allow) até o master restringir.
    if (this.isNova || !this.data.etapa) {
      this.isCarregando = true;
      perfis$.subscribe({
        next: response => {
          this.isCarregando = false;
          this.aplicarPerfis(response);
          this.marcarTodasPermissoes();
        },
        error: () => {
          this.isCarregando = false;
          this.alertService.show(
            'Erro',
            'Não foi possível carregar os perfis. Feche e abra o cadastro novamente.',
            AlertType.error
          );
        },
      });
      return;
    }

    // Edição: só libera o corpo do modal quando perfis + configuração chegarem — evita o "flash".
    this.isCarregando = true;
    forkJoin({
      perfis: perfis$,
      config: this.crmEtapasKanbanService.getConfiguracao(this.data.etapa.idEtapaKanban),
    }).subscribe({
      next: ({ perfis, config }) => {
        this.aplicarPerfis(perfis);
        this.aplicarConfiguracao(config);
        this.isCarregando = false;
      },
      error: () => {
        this.isCarregando = false;
        this.alertService.show(
          'Erro',
          'Não foi possível carregar a configuração da etapa. Feche e abra o cadastro novamente.',
          AlertType.error
        );
      },
    });
  }

  // ----------------------------------------------------------------------
  // Aplicação dos dados carregados
  // ----------------------------------------------------------------------
  private aplicarPerfis(response: { isError: boolean; listaPerfil?: { perfil: Perfil }[] }): void {
    if (response.isError) {
      this.alertService.show(
        'Atenção',
        'Não foi possível carregar os perfis desta empresa.',
        AlertType.warning
      );
      return;
    }
    this.perfis = (response.listaPerfil ?? []).map(item => item.perfil);
  }

  private aplicarConfiguracao(response: GetCrmConfiguracaoEtapaResponse): void {
    if (response.isError) {
      this.alertService.show(
        'Atenção',
        response.errorDescription || 'Não foi possível carregar a configuração da etapa.',
        AlertType.warning
      );
      return;
    }

    this.campos = (response.campos ?? []).map(c => ({
      idCampoDefinicao: c.idCampoDefinicao,
      nome: c.nome,
      tipo: this.tipoIdToKey(c.idTipoCampo),
      obrigatorio: c.isObrigatorio,
      isEssencial: c.isSistemico,
      idTipoEntidadeVinculo: c.idTipoEntidadeVinculo,
      opcoes: (c.opcoes ?? [])
        .slice()
        .sort((a, b) => a.ordem - b.ordem)
        .map(o => ({ idCampoDefinicaoOpcao: o.idCampoDefinicaoOpcao, descricao: o.descricao })),
    }));

    (response.movimentacoes ?? []).forEach(m => {
      const destino = this.destinos.find(d => d.etapa.idEtapaKanban === m.idEtapaKanbanDestino);
      if (!destino) return;
      destino.config.selecionado = true;
      destino.config.perfis = m.idsPerfil ?? [];
    });

    this.permissoes = {};
    if ((response.permissoes ?? []).length === 0) {
      // Etapa ainda não configurada: default-allow — todos os perfis podem tudo.
      this.marcarTodasPermissoes();
      return;
    }

    (response.permissoes ?? []).forEach(p => {
      this.permissoes[p.idPerfil] = {};
      (p.idsAcao ?? []).forEach(idAcao => {
        const acao = this.acoes.find(a => a.id === idAcao);
        if (acao) this.permissoes[p.idPerfil][acao.key] = true;
      });
    });
  }

  private tipoIdToKey(idTipoCampo: number): string {
    return this.tiposCampo.find(t => t.id === idTipoCampo)?.key ?? 'text';
  }

  private tipoKeyToId(key: string): number {
    return this.tiposCampo.find(t => t.key === key)?.id ?? 1;
  }

  // ----------------------------------------------------------------------
  // Flags derivadas da etapa
  // ----------------------------------------------------------------------
  /** Encerrado: etapa terminal e especial, com regras próprias (story, Seção 3). */
  public get isEncerrado(): boolean {
    return !!this.data.etapa?.isEspecial;
  }

  /**
   * Reabrir e a etapa de retorno seguem is_final na API/banco, não isEspecial — uma etapa
   * final pode não ser a especial "Encerrado" (ex.: "Cancelado"). Lê o campo do formulário
   * (this.isFinal), não direto de data.etapa, para valer também numa etapa nova marcada como
   * final na mesma sessão de edição.
   */
  public get isEtapaFinal(): boolean {
    return this.isFinal;
  }

  /**
   * Etapa criada pelo sistema: não pode ser excluída. Mesma condição-raiz do
   * isEtapaPadrao do Compras.
   */
  public get isEtapaPadrao(): boolean {
    return !!this.data.etapa?.isFixo;
  }

  /** Etapa final não tem para onde mandar o card — a Seção 3 fica sem destinos. */
  public get permiteMovimentacoes(): boolean {
    return !this.data.etapa?.isFinal;
  }

  /** Nome da etapa padrão é fixo — o backend também recusa a alteração (mesma regra do Compras). */
  public get isNomeBloqueado(): boolean {
    return this.isEtapaPadrao;
  }

  // ----------------------------------------------------------------------
  // Seção 1 — Identidade
  // ----------------------------------------------------------------------
  public selecionarCor(c: string): void {
    this.cor = c;
  }

  public selecionarIcone(i: string): void {
    this.icone = i;
  }

  // ----------------------------------------------------------------------
  // Seção 2 — Campos da etapa
  // ----------------------------------------------------------------------
  public adicionarCampo(): void {
    this.campos.push({
      idCampoDefinicao: null,
      nome: '',
      tipo: 'text',
      obrigatorio: false,
      isEssencial: false,
      idTipoEntidadeVinculo: null,
      opcoes: [],
    });
  }

  public removerCampo(index: number): void {
    if (this.campos[index]?.isEssencial) return;
    this.campos.splice(index, 1);
  }

  public reordenarCampos(event: CdkDragDrop<CampoEtapa[]>): void {
    moveItemInArray(this.campos, event.previousIndex, event.currentIndex);
  }

  public isCampoComOpcoes(campo: CampoEtapa): boolean {
    return campo.tipo === 'select';
  }

  public isCampoVinculo(campo: CampoEtapa): boolean {
    return campo.tipo === 'link';
  }

  public adicionarOpcao(campo: CampoEtapa): void {
    if (!this.isCampoComOpcoes(campo)) return;
    campo.opcoes.push({ idCampoDefinicaoOpcao: null, descricao: '' });
  }

  public removerOpcao(campo: CampoEtapa, index: number): void {
    campo.opcoes.splice(index, 1);
  }

  public onTipoCampoChange(campo: CampoEtapa): void {
    if (this.isCampoVinculo(campo)) {
      campo.opcoes = [];
      if (!campo.idTipoEntidadeVinculo) campo.idTipoEntidadeVinculo = this.vinculoEntidades[0].id;
      return;
    }

    campo.idTipoEntidadeVinculo = null;

    if (!this.isCampoComOpcoes(campo)) {
      campo.opcoes = [];
      return;
    }

    if (!campo.opcoes.length) campo.opcoes = [{ idCampoDefinicaoOpcao: null, descricao: '' }];
  }

  public nomePerfil(idPerfil: number): string {
    return this.perfis.find(p => p.idPerfil === idPerfil)?.nome ?? String(idPerfil);
  }

  // ----------------------------------------------------------------------
  // Seção 3 — Movimentações permitidas
  // ----------------------------------------------------------------------
  public get totalDestinos(): number {
    return this.destinos.filter(d => d.config.selecionado).length;
  }

  public get destinosPrincipais(): DestinoEtapa[] {
    return this.destinos.filter(d => !d.etapa.isEspecial);
  }

  public get destinosEspeciais(): DestinoEtapa[] {
    return this.destinos.filter(d => d.etapa.isEspecial);
  }

  /** Sem checkbox: o card inteiro alterna o destino e a borda colorida indica o estado (CA07). */
  public toggleDestino(d: { config: DestinoConfig }): void {
    d.config.selecionado = !d.config.selecionado;
    if (!d.config.selecionado) d.config.perfis = [];
  }

  public removerPerfilDestino(d: { config: DestinoConfig }, idPerfil: number): void {
    d.config.perfis = d.config.perfis.filter(id => id !== idPerfil);
  }

  // ----------------------------------------------------------------------
  // Seção 4 — Permissões por perfil
  // ----------------------------------------------------------------------
  /**
   * Reabrir só existe na etapa final — nas demais a coluna nem aparece na matriz.
   */
  public get acoesVisiveis(): AcaoEtapa[] {
    return this.acoes.filter(a => a.key !== 'reabrir' || this.isEtapaFinal);
  }

  /** Default-allow: todo perfil pode tudo até o master restringir (story, ponto 4). */
  private marcarTodasPermissoes(): void {
    this.permissoes = {};
    this.perfis.forEach(p => {
      this.permissoes[p.idPerfil] = {};
      this.acoesVisiveis.forEach(a => {
        this.permissoes[p.idPerfil][a.key] = true;
      });
    });
  }

  public get perfisFiltrados(): Perfil[] {
    const termo = this.filtroPerfil.trim().toLowerCase();
    return this.perfis.filter(p => p.nome.toLowerCase().includes(termo));
  }

  /**
   * Sem "Visualizar" o perfil não enxerga o card, então as demais ações não se
   * sustentam: marcar uma delas liga o Visualizar, e desmarcá-lo desliga todas.
   */
  public togglePermissao(idPerfil: number, acao: string): void {
    this.permissoes[idPerfil] = this.permissoes[idPerfil] || {};
    const novoValor = !this.permissoes[idPerfil][acao];
    this.permissoes[idPerfil][acao] = novoValor;
    this.aplicarDependenciaVisualizar(idPerfil, acao, novoValor);
  }

  private aplicarDependenciaVisualizar(idPerfil: number, acao: string, valor: boolean): void {
    if (acao === 'view') {
      if (!valor) {
        this.acoesVisiveis
          .filter(a => a.key !== 'view')
          .forEach(a => (this.permissoes[idPerfil][a.key] = false));
      }
      return;
    }

    if (valor) this.permissoes[idPerfil]['view'] = true;
  }

  public estadoColuna(acao: string): 'all' | 'some' | 'none' {
    const perfis = this.perfisFiltrados;
    if (perfis.length === 0) return 'none';

    const marcados = perfis.filter(p => this.permissoes[p.idPerfil]?.[acao]).length;
    if (marcados === 0) return 'none';
    return marcados === perfis.length ? 'all' : 'some';
  }

  public toggleColuna(acao: string): void {
    const marcar = this.estadoColuna(acao) !== 'all';
    this.perfisFiltrados.forEach(p => {
      this.permissoes[p.idPerfil] = this.permissoes[p.idPerfil] || {};
      this.permissoes[p.idPerfil][acao] = marcar;
      this.aplicarDependenciaVisualizar(p.idPerfil, acao, marcar);
    });
  }

  /** Etapas ativas, exceto a própria etapa final (story, Seção 3). */
  public get etapasRetornoReabertura(): CrmEtapaOportunidade[] {
    return (this.data.todasEtapas || []).filter(e => !e.isFinal && e.isAtivo);
  }

  // ----------------------------------------------------------------------
  // Rodapé
  // ----------------------------------------------------------------------
  private validar(): string | null {
    if (!this.nome.trim()) return 'Informe o nome da coluna.';

    if (this.nome.trim().length > this.NOME_MAX_LENGTH) {
      return `O nome da coluna deve ter no máximo ${this.NOME_MAX_LENGTH} caracteres.`;
    }

    const semNome = this.campos.find(c => !c.nome.trim());
    if (semNome) return 'Todos os campos da etapa precisam de um nome.';

    // campo_definicao_etapa_nome_uk: o backend recusa nomes repetidos na mesma etapa.
    const nomes = this.campos.map(c => c.nome.trim().toLowerCase());
    const duplicado = nomes.find((nome, i) => nomes.indexOf(nome) !== i);
    if (duplicado) return 'Não é permitido mais de um campo com o mesmo nome na etapa.';

    const dropdownVazio = this.campos.find(
      c => this.isCampoComOpcoes(c) && c.opcoes.filter(o => o.descricao.trim()).length === 0
    );
    if (dropdownVazio) {
      return `O campo "${dropdownVazio.nome}" é um dropdown e precisa de ao menos uma opção.`;
    }

    // campo_definicao_vinculo_chk: tipo Vínculo exige a entidade preenchida.
    const vinculoSemEntidade = this.campos.find(c => this.isCampoVinculo(c) && !c.idTipoEntidadeVinculo);
    if (vinculoSemEntidade) {
      return `O campo "${vinculoSemEntidade.nome}" precisa de uma entidade de vínculo.`;
    }

    // Opções repetidas violariam campo_definicao_opcao_uk.
    const opcaoDuplicada = this.campos.find(c => {
      const descricoes = c.opcoes.map(o => o.descricao.trim().toLowerCase()).filter(d => d);
      return descricoes.some((d, i) => descricoes.indexOf(d) !== i);
    });
    if (opcaoDuplicada) {
      return `O campo "${opcaoDuplicada.nome}" tem opções repetidas.`;
    }

    return null;
  }

  public salvar(): void {
    const erro = this.validar();
    if (erro) {
      // Erro de validação em modal de alerta (um único botão), para dar ênfase.
      this.dialog.open(ConfirmModalComponent, {
        data: { title: 'Atenção', description: erro, buttonConfirm: 'Entendi' },
      });
      return;
    }

    const result = this.montarResultado();

    // Sem callback (uso isolado do modal), devolve o payload e deixa o chamador decidir.
    if (!this.data.onSalvar) {
      this.dialogRef.close(result);
      return;
    }

    this.isSalvando = true;
    this.data.onSalvar(result).subscribe({
      next: ok => {
        this.isSalvando = false;
        if (ok) this.dialogRef.close(result);
      },
      error: () => {
        this.isSalvando = false;
      },
    });
  }

  private montarResultado(): EtapaOportunidadeModalResult {
    const etapaResultado: CrmEtapaOportunidade = {
      idEtapaKanban: this.data.etapa?.idEtapaKanban ?? 0,
      chave: this.chave || this.gerarChave(this.nome),
      nome: this.nome.trim(),
      descricao: this.descricao.trim(),
      cor: this.cor,
      tint: this.cor + '22',
      icone: this.icone,
      ordem: this.data.etapa?.ordem ?? 0,
      // Etapa criada pelo master nasce reordenável e inativável.
      isFixo: this.data.etapa?.isFixo ?? false,
      isOpcional: this.data.etapa?.isOpcional ?? true,
      isAtivo: this.data.etapa?.isAtivo ?? true,
      isFinal: this.data.etapa?.isFinal ?? false,
      isEspecial: this.data.etapa?.isEspecial ?? false,
      idEtapaKanbanRetornoReabertura: this.isEtapaFinal ? this.idEtapaRetornoReabertura : null,
      qtdCampos: this.campos.length,
      qtdDestinos: this.totalDestinos,
      qtdCards: this.data.etapa?.qtdCards ?? 0,
    };

    return {
      salvou: true,
      isNova: this.isNova,
      etapa: etapaResultado,
      campos: this.montarCampos(),
      // Etapa final não tem saída: manda lista vazia em vez do que estiver na tela.
      movimentacoes: this.permiteMovimentacoes
        ? this.destinos
            .filter(d => d.config.selecionado)
            .map(d => ({ idEtapaKanbanDestino: d.etapa.idEtapaKanban, idsPerfil: d.config.perfis }))
        : [],
      permissoes: this.perfis.map(p => ({
        idPerfil: p.idPerfil,
        idsAcao: this.acoesVisiveis.filter(a => this.permissoes[p.idPerfil]?.[a.key]).map(a => a.id),
      })),
      idEtapaRetornoReabertura: this.isEtapaFinal ? this.idEtapaRetornoReabertura : null,
    };
  }

  private montarCampos(): CrmCampoDefinicaoRequest[] {
    return this.campos.map((campo, index) => {
      const idTipoCampo = this.tipoKeyToId(campo.tipo);
      return {
        idCampoDefinicao: campo.idCampoDefinicao ?? null,
        idTipoCampo,
        nome: campo.nome.trim(),
        isObrigatorio: campo.obrigatorio,
        descricao: null,
        idTipoEntidadeVinculo: this.isCampoVinculo(campo) ? (campo.idTipoEntidadeVinculo ?? null) : null,
        ordem: index + 1,
        opcoes: this.isCampoComOpcoes(campo)
          ? campo.opcoes
              .filter(o => o.descricao.trim())
              .map((o, i) => ({
                idCampoDefinicaoOpcao: o.idCampoDefinicaoOpcao ?? null,
                descricao: o.descricao.trim(),
                ordem: i + 1,
              }))
          : [],
      };
    });
  }

  public excluir(): void {
    if (this.isEtapaPadrao || !this.data.etapa) return;
    this.dialogRef.close({ excluiu: true, etapa: this.data.etapa });
  }

  public fechar(): void {
    this.dialogRef.close();
  }

  // Mesma normalização do backend (EtapaKanbanChaveHelper): slug do nome, sem acentos.
  private gerarChave(nome: string): string {
    return nome
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }
}
